import { useMemo } from 'react'

/**
 * Inline editor for cards.recurrence_rule (simple format).
 * Format produced:
 *   ""              → no recurrence
 *   "DAILY"         → every day
 *   "WEEKLY:MO,WE,FR" → on listed weekdays
 *   "MONTHLY:15"    → 15th of every month
 *
 * Props:
 *   rule:     current rule string (or '')
 *   onChange: (newRule: string) => void
 */
const WEEKDAY_LABELS = [
    { code: 'MO', label: 'Lu' },
    { code: 'TU', label: 'Ma' },
    { code: 'WE', label: 'Mi' },
    { code: 'TH', label: 'Jo' },
    { code: 'FR', label: 'Vi' },
    { code: 'SA', label: 'Sâ' },
    { code: 'SU', label: 'Du' },
]

function parseRule(rule) {
    if (!rule) return { kind: '', arg: '' }
    const [kind, arg = ''] = rule.split(':')
    return { kind: kind.toUpperCase(), arg: arg.toUpperCase() }
}

export default function RecurrenceEditor({ rule = '', onChange, disabled = false }) {
    const parsed = useMemo(() => parseRule(rule), [rule])
    const selectedDays = parsed.kind === 'WEEKLY'
        ? new Set(parsed.arg.split(',').filter(Boolean))
        : new Set()

    const setKind = (newKind) => {
        if (!newKind) { onChange?.(''); return }
        if (newKind === 'DAILY') { onChange?.('DAILY'); return }
        if (newKind === 'WEEKLY') {
            // Default to Mon–Fri when switching into WEEKLY
            onChange?.('WEEKLY:MO,TU,WE,TH,FR')
            return
        }
        if (newKind === 'MONTHLY') {
            const today = new Date().getDate()
            onChange?.(`MONTHLY:${today}`)
            return
        }
    }

    const toggleDay = (code) => {
        const next = new Set(selectedDays)
        if (next.has(code)) next.delete(code)
        else next.add(code)
        if (next.size === 0) { onChange?.(''); return }
        // Preserve canonical order Mo→Su
        const ordered = WEEKDAY_LABELS.filter(d => next.has(d.code)).map(d => d.code)
        onChange?.(`WEEKLY:${ordered.join(',')}`)
    }

    const setMonthDay = (n) => {
        const day = Math.max(1, Math.min(31, parseInt(n, 10) || 1))
        onChange?.(`MONTHLY:${day}`)
    }

    return (
        <div className="recur-editor">
            <select
                className="inline-input cd-field-select"
                value={parsed.kind || ''}
                onChange={(e) => setKind(e.target.value)}
                disabled={disabled}
                aria-label="Frecvență repetare"
            >
                <option value="">Nu se repetă</option>
                <option value="DAILY">Zilnic</option>
                <option value="WEEKLY">Săptămânal</option>
                <option value="MONTHLY">Lunar</option>
            </select>

            {parsed.kind === 'WEEKLY' && (
                <div className="recur-weekday-row" role="group" aria-label="Zile săptămână">
                    {WEEKDAY_LABELS.map(d => (
                        <button
                            key={d.code}
                            type="button"
                            className={`recur-weekday-chip ${selectedDays.has(d.code) ? 'active' : ''}`.trim()}
                            onClick={() => toggleDay(d.code)}
                            disabled={disabled}
                            title={d.code}
                        >
                            {d.label}
                        </button>
                    ))}
                </div>
            )}

            {parsed.kind === 'MONTHLY' && (
                <div className="recur-monthly-row">
                    <span className="recur-monthly-label">În ziua</span>
                    <input
                        type="number"
                        min={1}
                        max={31}
                        value={parseInt(parsed.arg, 10) || 1}
                        onChange={(e) => setMonthDay(e.target.value)}
                        disabled={disabled}
                        className="recur-monthly-input"
                    />
                    <span className="recur-monthly-label">a lunii</span>
                </div>
            )}
        </div>
    )
}
